import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react'
import { cx } from './cx.ts'

export interface OtpInputProps {
  value: string
  onChange: (value: string) => void
  /** Called once all digits are filled (typed, pasted or autofilled). */
  onComplete?: (value: string) => void
  length?: number
  invalid?: boolean
  disabled?: boolean
  autoFocus?: boolean
  label?: string
  describedBy?: string
  className?: string
}

/**
 * One-time code entry: auto-advance, backspace steps back, arrows move, paste/SMS autofill fills all
 * cells. Digits only; the first cell carries autocomplete="one-time-code".
 */
export function OtpInput({ value, onChange, onComplete, length = 6, invalid, disabled, autoFocus, label = 'Sign-in code', describedBy, className }: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([])
  const digits = Array.from({ length }, (_, i) => value[i] ?? '')

  useEffect(() => {
    if (autoFocus) refs.current[Math.min(value.length, length - 1)]?.focus()
  }, []) // mount only

  const set = (next: string, focusAt?: number) => {
    const clean = next.replace(/\D/g, '').slice(0, length)
    onChange(clean)
    if (focusAt !== undefined) refs.current[Math.max(0, Math.min(length - 1, focusAt))]?.focus()
    if (clean.length === length) onComplete?.(clean)
  }

  const onInput = (i: number, raw: string) => {
    const d = raw.replace(/\D/g, '')
    if (!d) return
    if (d.length > 1) {
      // autofill or a fast typist: spread from this cell
      const merged = (value.slice(0, i) + d).slice(0, length)
      set(merged, merged.length)
      return
    }
    const arr = digits.slice()
    arr[i] = d
    const merged = arr.join('').slice(0, length)
    set(merged, i + 1)
  }

  const onKeyDown = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      if (digits[i]) set(value.slice(0, i) + value.slice(i + 1), i)
      else if (i > 0) set(value.slice(0, i - 1) + value.slice(i), i - 1)
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      refs.current[Math.max(0, i - 1)]?.focus()
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      refs.current[Math.min(length - 1, i + 1)]?.focus()
    }
  }

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '')
    if (!text) return
    e.preventDefault()
    set(text.slice(0, length), Math.min(length - 1, text.length))
  }

  return (
    <div className={cx('rt-otp', className)} role="group" aria-label={label} data-invalid={invalid ? '' : undefined} data-disabled={disabled ? '' : undefined}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el
          }}
          className="rt-otp__cell"
          value={d}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={i === 0 ? length : 1}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          aria-label={`Digit ${i + 1} of ${length}`}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          disabled={disabled}
          onChange={(e) => onInput(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          onPaste={onPaste}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  )
}
